import React from "react";
import { Button } from "react-bootstrap";
import { Select, MenuItem, FormControl } from "@mui/material";

interface PaginationProps {
  pageIndex: number;
  pageSize: number;
  totalRecords: number;
  onPageChange: (page: number) => void;
  onPageSizeChange: (size: number) => void;
  pageSizeOptions?: number[];
}

const Pagination: React.FC<PaginationProps> = ({
  pageIndex,
  pageSize,
  totalRecords,
  onPageChange,
  onPageSizeChange,
  pageSizeOptions = [10, 20, 30, 50],
}) => {
  const themeColor = '#3969E7'; // Primary color from tableStyles (same as ActionButton)
  const totalPages = Math.ceil(totalRecords / pageSize);
  const maxPageButtons = 5;

  const getPageNumbers = React.useMemo(() => {
    const pages: number[] = [];
    if (totalPages <= maxPageButtons) {
      for (let i = 0; i < totalPages; i++) pages.push(i);
    } else if (pageIndex <= 2) {
      pages.push(0, 1, 2, -1, totalPages - 1);
    } else if (pageIndex >= totalPages - 3) {
      pages.push(0, -1, totalPages - 3, totalPages - 2, totalPages - 1);
    } else {
      pages.push(
        0,
        -1,
        pageIndex - 1,
        pageIndex,
        pageIndex + 1,
        -1,
        totalPages - 1
      );
    }
    return pages;
  }, [pageIndex, totalPages]);

  return (
    <React.Fragment>
      <style>
        {`
          .pagination .page-link.active {
            background-color: ${themeColor} !important;
            border-color: ${themeColor} !important;
            color: #fff !important;
          }
          .pagination .page-link:hover:not(.active):not(:disabled) {
            background-color: ${themeColor}20 !important;
            border-color: ${themeColor} !important;
            color: ${themeColor} !important;
          }
          .pagination .page-link:focus {
            box-shadow: 0 0 0 0.2rem ${themeColor}40 !important;
          }
          .pagination .page-link {
            color: ${themeColor} !important;
          }
          .pagination .page-item.disabled .page-link {
            color: #6c757d !important;
            pointer-events: none;
            cursor: auto;
          }
          .MuiSelect-select {
            padding: 2px 28px 2px 8px !important;
            font-size: 0.75rem !important;
            min-height: auto !important;
          }
          .MuiOutlinedInput-notchedOutline {
            border-color: #ced4da !important;
          }
          .MuiSelect-select:focus + .MuiOutlinedInput-notchedOutline {
            border-color: ${themeColor} !important;
            border-width: 1px !important;
          }
          .Mui-focused .MuiOutlinedInput-notchedOutline {
            border-color: ${themeColor} !important;
            border-width: 1px !important;
          }
          .MuiPaper-root .MuiMenuItem-root:hover {
            background-color: ${themeColor}20 !important;
            color: ${themeColor} !important;
          }
          .MuiPaper-root .MuiMenuItem-root.Mui-selected {
            background-color: ${themeColor} !important;
            color: #fff !important;
          }
          .MuiPaper-root .MuiMenuItem-root.Mui-selected:hover {
            background-color: ${themeColor} !important;
            color: #fff !important;
          }
        `}
      </style>
      <div className="d-flex flex-wrap align-items-center gap-2 justify-content-between w-100">
        {/* Results Info */}
        <div className="text-muted">
          Showing{" "}
          <span className="fw-semibold">
            {totalRecords === 0 ? 0 : pageIndex * pageSize + 1}
          </span>{" "}
          -{" "}
          <span className="fw-semibold">
            {Math.min((pageIndex + 1) * pageSize, totalRecords)}
          </span>{" "}
          of <span className="fw-semibold">{totalRecords}</span> Results
        </div>

        {/* Page Navigation */}
        <div className="d-flex flex-wrap align-items-center gap-2">
          <ul className="pagination pagination-separated mb-0">
          {/* Previous */}
          <li className={`page-item ${pageIndex === 0 ? "disabled" : ""}`}>
            <Button
              variant="link"
              className="page-link"
              disabled={pageIndex === 0}
              onClick={() => onPageChange(pageIndex - 1)}
            >
              &lt;
            </Button>
          </li>

          {/* Page numbers */}
          {getPageNumbers.map((page, idx) => (
            <li key={idx} className="page-item">
              {page === -1 ? (
                <span className="page-link">...</span>
              ) : (
                <Button
                  variant="link"
                  className={`page-link ${
                    pageIndex === page ? "active" : ""
                  }`}
                  onClick={() => onPageChange(page)}
                >
                  {page + 1}
                </Button>
              )}
            </li>
          ))}

          {/* Next */}
          <li
            className={`page-item ${
              pageIndex >= Math.ceil(totalRecords / pageSize) - 1
                ? "disabled"
                : ""
            }`}
          >
            <Button
              variant="link"
              className="page-link"
              disabled={pageIndex >= Math.ceil(totalRecords / pageSize) - 1}
              onClick={() => onPageChange(pageIndex + 1)}
            >
              &gt;
            </Button>
          </li>
        </ul>

        {/* Page Size Select */}
        <div className="ms-2">
          <FormControl size="small" sx={{ minWidth: 85 }}>
            <Select
              value={pageSize}
              onChange={(e) => {
                const value = e.target.value;
                onPageSizeChange(Number(value));
              }}
              sx={{
                height: "28px",
                fontSize: "0.75rem",
                fontFamily: "Poppins, sans-serif",
                "& .MuiOutlinedInput-notchedOutline": {
                  borderColor: "#ced4da",
                },
                "&:hover .MuiOutlinedInput-notchedOutline": {
                  borderColor: themeColor,
                },
                "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                  borderColor: themeColor,
                  borderWidth: "1px",
                },
              }}
              MenuProps={{
                PaperProps: {
                  sx: {
                    fontFamily: "Poppins, sans-serif",
                    "& .MuiMenuItem-root": {
                      minHeight: "32px",
                      padding: "4px 12px",
                      fontSize: "0.75rem",
                      fontFamily: "Poppins, sans-serif",
                    },
                    "& .MuiMenuItem-root:hover": {
                      backgroundColor: `${themeColor}20`,
                      color: themeColor,
                    },
                    "& .MuiMenuItem-root.Mui-selected": {
                      backgroundColor: themeColor,
                      color: "#fff",
                    },
                    "& .MuiMenuItem-root.Mui-selected:hover": {
                      backgroundColor: themeColor,
                      color: "#fff",
                    },
                  },
                },
              }}
            >
              {pageSizeOptions.map((size) => (
                <MenuItem key={size} value={size}>
                  Show {size}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </div>
      </div>
      </div>
    </React.Fragment>
  );
};

export default Pagination;
